import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type {
  DashboardTeam,
  LeaveListItem,
  LeaveTypeId,
  Role,
} from '@lms/shared-types'
import { PageHead } from '../components/primitives/PageHead'
import { Severity } from '../components/primitives/Severity'
import { Banner } from '../components/primitives/Banner'
import { Card } from '../components/primitives/Card'
import { Avatar } from '../components/primitives/Avatar'
import { FadeIn } from '../components/primitives/FadeIn'
import { LeaveTypePill } from '../components/primitives/LeaveTypePill'
import { Segmented } from '../components/primitives/Segmented'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { OutThisWeekStrip } from '../components/primitives/OutThisWeekStrip'
import { DateRangeFilter, FORWARD_PRESETS } from '../components/primitives/DateRangeFilter'
import { isoMondayOf } from '../components/manager-week'
import { ManagerQueueCard } from '../components/manager/ManagerQueueCard'
import { CoverageRail } from '../components/manager/CoverageRail'
import { RiskBandPanel } from '../components/insights/RiskBandPanel'
import { getDashboard } from '../api/dashboard'
import { listCalendarTeams, type CalendarTeamOption } from '../api/calendar'
import { listLeaves } from '../api/leaves'
import { useSession } from '../auth/auth-context'
import { fmtDateRange } from '../lib/format'
import {
  useEffectiveEmployeeId,
  useEffectiveRole,
} from '../components/shell/effective-employee'
import { useMoment } from '../lib/moment-context'
import { displayName } from '../lib/display-name'
import { flags } from '../lib/feature-flags'

function isoToday(): string {
  return new Date().toISOString().slice(0, 10)
}

function isoInDays(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

const HR_PLUS = new Set<Role>(['hr', 'director', 'exec_director'])

const LEAVE_TYPE_META: Record<LeaveTypeId, { label: string; color: string }> = {
  annual: { label: 'Annual', color: 'var(--leave-annual)' },
  medical: { label: 'Medical', color: 'var(--leave-sick)' },
  hospitalization: { label: 'Hospitalization', color: 'var(--leave-hospitalization)' },
  birthday: { label: 'Birthday', color: 'var(--leave-birthday)' },
  compassionate: { label: 'Compassionate', color: 'var(--leave-compassionate)' },
  maternity: { label: 'Maternity', color: 'var(--leave-maternity)' },
  paternity: { label: 'Paternity', color: 'var(--leave-paternity)' },
  marriage: { label: 'Marriage', color: 'var(--leave-family)' },
  marriage_family_care: { label: 'Marriage & Family Care', color: 'var(--leave-family)' },
  unpaid: { label: 'Unpaid', color: 'var(--leave-unpaid)' },
  replacement: { label: 'Replacement', color: 'var(--leave-replacement)' },
}

export function ManagerDashboardPage(): JSX.Element {
  const { user } = useSession()
  const realRole: Role = (user?.role as Role) ?? 'employee'
  const effectiveRole = useEffectiveRole(realRole)
  const effectiveEmployeeId = useEffectiveEmployeeId(user?.id ?? null)
  // effectiveRole (not realRole) so viewing-as a lower role narrows the queue
  // scope + team selector to that person, not the real admin's company scope.
  const isHrPlus = HR_PLUS.has(effectiveRole)
  const isHod = Boolean(user?.isHod)
  const [team, setTeam] = useState<string>('')
  const { moment: opMoment, reason: momentReason } = useMoment()

  const defaultFrom = useMemo(isoToday, [])
  const defaultTo = useMemo(() => isoInDays(30), [])
  const [dateFrom, setDateFrom] = useState<string>(defaultFrom)
  const [dateTo, setDateTo] = useState<string>(defaultTo)

  const dateFilterActive = flags.managerDashboardDateFilter

  const isManager = effectiveRole === 'manager'

  // Managers always get this week's coverage payload (Hafiz's "what
  // about today / this week" lens) so thin-day context is baked into
  // each queue row. HR / HOD keep the 30-day steady-state shape — the
  // multi-team capacity list is their primary lens.
  const todayMonday = useMemo(() => isoMondayOf(new Date()), [])
  const weekStart = isManager ? todayMonday : null

  const dashboardQuery = useQuery({
    queryKey: [
      'dashboard',
      team || null,
      effectiveEmployeeId,
      effectiveRole,
      dateFilterActive ? dateFrom : null,
      dateFilterActive ? dateTo : null,
      weekStart,
    ],
    queryFn: () =>
      getDashboard({
        team: team || undefined,
        horizonDays: 30,
        ...(dateFilterActive && !isManager ? { fromDate: dateFrom, toDate: dateTo } : {}),
        ...(weekStart ? { weekStart } : {}),
      }),
    enabled: Boolean(user),
    refetchInterval: 60_000,
  })

  const teamsQuery = useQuery({
    queryKey: ['calendar', 'teams'],
    queryFn: listCalendarTeams,
    enabled: Boolean(user) && (isHrPlus || isHod),
  })

  // Managers see the queue *as* the dashboard — fetch up to 50 so every
  // pending request gets an inline approve/reject row. HR/HOD keep the
  // 5-row preview card since their primary lens is coverage.
  const pendingPreviewQuery = useQuery({
    queryKey: [
      'requests',
      isManager ? 'queue' : 'preview',
      team || null,
      effectiveEmployeeId,
    ],
    queryFn: () =>
      listLeaves({
        scope: isHrPlus ? 'all' : 'team',
        status: 'pending',
        limit: isManager ? 50 : 5,
      }),
    enabled: Boolean(user),
  })


  const data = dashboardQuery.data
  const showSelector = (isHrPlus || isHod) && (teamsQuery.data?.length ?? 0) > 1

  // Surface a conflict banner when there are teams at full capacity
  const atCapTeams = useMemo(
    () => (data?.teams ?? []).filter((t) => t.onLeaveToday >= t.slotsToday),
    [data],
  )
  const hasPressure = atCapTeams.length > 0

  const eyebrow = isHrPlus ? 'HR' : isHod ? 'Head of Department' : 'Manager'

  // "In selected window" replaces "Out this week" when the date filter is
  // (outWindowLabel + outWindowCount were tied to the "Out this week"
  // stat tile, dropped 2026-05-17 in the focus-pass declutter.)

  const peakBanner =
    opMoment === 'peak' ? (
      <FadeIn duration="base">
        <Banner
          kind="warning"
          title="Peak window active"
          subtitle={momentReason ?? 'Team coverage is reduced — coordinate closely before approving leave.'}
        />
      </FadeIn>
    ) : null

  // (Director-decisions banner removed: Directors now land on
  // `/director` (DirectorDashboardPage), routed by the sidebar's
  // role-aware Dashboard entry. The manager surface stays focused on
  // manager concerns.)

  // (Removed 2026-05-20) The "Check a week" toggle is gone — the manager
  // dashboard IS the week-aware queue, no opt-in. HR/HOD still reach the
  // deep-dive layout via a manual `?weekStart=` query param, but it isn't
  // surfaced as a button on this page.

  const pageTitle = 'Dashboard'
  const headActions =
    isManager
      ? undefined
      : dateFilterActive
        ? (
            <DateRangeFilter
              from={dateFrom}
              to={dateTo}
              onChange={(f, t) => {
                setDateFrom(f)
                setDateTo(t)
              }}
              presets={FORWARD_PRESETS}
            />
          )
        : undefined

  return (
    // For managers: the queue is the page, coverage condenses to a rail.
    // For HR/HOD: coverage is the at-a-glance signal — per-team capacity
    // list stays inline alongside the queue preview + who's out strip.
    <div data-template="manager-dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }} data-testid="manager-dashboard">
      {hasPressure || peakBanner ? (
        <div data-slot="banner">
          {peakBanner}
          {hasPressure && !isManager ? (
            <Banner
              kind="warning"
              title={`${atCapTeams.length} team${atCapTeams.length === 1 ? '' : 's'} at capacity today`}
              subtitle={`${atCapTeams.map((t) => t.name).join(', ')} — coverage slots fully used.`}
            />
          ) : null}
        </div>
      ) : null}

      <div data-slot="head">
        <PageHead eyebrow={eyebrow} title={pageTitle} actions={headActions} />
        {showSelector ? (
          <TeamSelector
            options={teamsQuery.data ?? []}
            value={team}
            onChange={setTeam}
          />
        ) : null}
      </div>

      {dashboardQuery.isLoading || !data ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <ThinkingDots label="Loading dashboard" />
        </div>
      ) : isManager ? (
        <FadeIn rise={4} duration="slow">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ManagerQueueCard
              leaves={pendingPreviewQuery.data ?? []}
              weekDays={data.weekTriage?.days}
              leaveTypeMeta={LEAVE_TYPE_META}
              onRefetch={() => {
                void pendingPreviewQuery.refetch()
                void dashboardQuery.refetch()
              }}
            />
            {/* "Who's out this week" answers Hafiz's mental model — the
                today/this-week lens belongs on the manager dashboard, not
                only on HR/HOD's. Gap-analysis manager round-1 finding #3. */}
            <OutThisWeekStrip
              items={data.whoIsOutThisWeek}
              today={new Date(data.generatedAt)}
            />
            <CoverageRail
              team={data.teams[0]}
              weekDays={data.weekTriage?.days}
              weekStart={weekStart ?? undefined}
              horizonDays={data.horizonDays}
            />
            <RiskBandPanel />
          </div>
        </FadeIn>
      ) : (
        <FadeIn rise={4} duration="slow">
          {/* HR / HOD steady state: coverage is the primary lens. Queue
              stays as a 5-row preview that links into /manager/requests,
              alongside who's out this week and per-team capacity. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PendingPreview leaves={pendingPreviewQuery.data ?? []} />
            <OutThisWeekStrip
              items={data.whoIsOutThisWeek}
              today={new Date(data.generatedAt)}
            />
            <TeamCapacityList teams={data.teams} horizonDays={data.horizonDays} />
            <RiskBandPanel />
          </div>
        </FadeIn>
      )}
    </div>
  )
}

// ── Team selector ─────────────────────────────────────────────────────────────

interface TeamSelectorProps {
  options: ReadonlyArray<CalendarTeamOption>
  value: string
  onChange: (next: string) => void
}

function TeamSelector({ options, value, onChange }: TeamSelectorProps): JSX.Element {
  const segmentOptions = useMemo(
    () => [
      { value: '', label: 'All teams' },
      ...options.map((t) => ({ value: t.id, label: t.name })),
    ],
    [options],
  )
  return (
    <div data-testid="team-selector" style={{ marginBottom: 16 }}>
      <Segmented
        options={segmentOptions}
        value={value}
        onChange={onChange}
        ariaLabel="Filter by team"
      />
    </div>
  )
}

// ── Team capacity list ────────────────────────────────────────────────────────

interface TeamCapacityListProps {
  teams: ReadonlyArray<DashboardTeam>
  horizonDays: number
}

function TeamCapacityList({
  teams,
  horizonDays,
}: TeamCapacityListProps): JSX.Element {
  return (
    <Card padding="14px 16px" style={{ marginBottom: 20 }}>
      <div
        data-testid="team-capacity"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 12,
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {`Coverage · next ${horizonDays} days`}
        </h2>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>slots used</span>
      </div>

      {teams.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>No teams visible.</div>
      ) : (
        <ul role="list" style={{ display: 'flex', flexDirection: 'column' }}>
          {teams.map((t) => (
            <TeamCapacityRow key={t.id} team={t} />
          ))}
        </ul>
      )}
    </Card>
  )
}

interface TeamCapacityRowProps {
  team: DashboardTeam
}

function TeamCapacityRow({ team }: TeamCapacityRowProps): JSX.Element {
  const slotsRatio = team.slotsToday > 0 ? team.onLeaveToday / team.slotsToday : 0
  const pct = Math.min(100, Math.round(slotsRatio * 100))
  const full = team.onLeaveToday >= team.slotsToday
  const isStress = full || pct >= 80
  const fillColor = full ? 'var(--danger)' : pct >= 60 ? 'var(--warning)' : 'var(--success)'

  return (
    <li
      data-testid="team-capacity-row"
      data-team-id={team.id}
      style={{
        padding: '12px 0',
        borderBottom: '1px solid var(--line-subtle)',
        display: 'grid',
        gridTemplateColumns: '180px 1fr 140px',
        gap: 12,
        alignItems: 'center',
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{team.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{team.memberCount} members</div>
      </div>
      <div>
        <div
          style={{
            height: 8,
            background: 'var(--paper-sunken)',
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: fillColor,
              borderRadius: 999,
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 4,
            fontSize: 11,
            color: 'var(--text-3)',
          }}
        >
          <span>
            <strong style={{ color: 'var(--text)' }}>
              {team.onLeaveToday}/{team.slotsToday}
            </strong>{' '}
            slots used today
          </span>
          <span>{team.pendingCount} pending</span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {isStress ? (
          <Severity kind="high">{full ? 'Full' : 'High'}</Severity>
        ) : pct >= 40 ? (
          <Severity kind="medium">{team.slotsToday - team.onLeaveToday} open</Severity>
        ) : (
          <Severity kind="info">{team.slotsToday - team.onLeaveToday} open</Severity>
        )}
      </div>
    </li>
  )
}

// ── Pending preview ───────────────────────────────────────────────────────────

interface PendingPreviewProps {
  leaves: ReadonlyArray<LeaveListItem>
}

function PendingPreview({ leaves }: PendingPreviewProps): JSX.Element {
  return (
    <Card padding="14px 16px">
      <div
        data-testid="pending-preview"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 10,
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          Queue preview
        </h2>
        <Link
          to="/manager/requests"
          style={{ fontSize: 12, color: 'var(--steel-blue)', fontWeight: 600 }}
        >
          See all →
        </Link>
      </div>
      {leaves.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Nothing waiting on you.</div>
      ) : (
        <ul role="list" style={{ display: 'flex', flexDirection: 'column' }}>
          {leaves.map((l) => {
            const meta = LEAVE_TYPE_META[l.type]
            return (
              <li
                key={l.id}
                data-testid="pending-preview-row"
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--line-subtle)',
                }}
              >
                <Avatar name={displayName(l.employee)} size={28} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {displayName(l.employee)}
                </span>
                <LeaveTypePill
                  leaveTypeId={l.type}
                  label={meta.label}
                  color={meta.color}
                  size="sm"
                />
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {fmtDateRange(l.startDate, l.endDate)}
                </span>
                {l.isEmergency ? <Severity kind="high">Emergency</Severity> : null}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
